// frontend/src/stores/authStore.ts
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { logoutAPI } from '../services/authService';

// Funções auxiliares para gerenciar credenciais "lembrar-me" usando cookies
const REMEMBER_KEYS = {
  USER_ID: 'kadesh_remember_userId',
  USERNAME: 'kadesh_remember_username',
  PASSWORD: 'kadesh_remember_password'
};

// Funções helper para cookies
const setCookie = (name: string, value: string, days: number = 365) => {
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Strict`;
};

const getCookie = (name: string): string | null => {
  const nameEQ = name + "=";
  const ca = document.cookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
};

const deleteCookie = (name: string) => {
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;
};

export const rememberMeUtils = {
  save: (userId: string, username: string, password?: string) => {
    // Só salva se os valores não forem vazios
    if (userId && userId.trim()) {
      setCookie(REMEMBER_KEYS.USER_ID, userId.trim());
    }
    if (username && username.trim()) {
      setCookie(REMEMBER_KEYS.USERNAME, username.trim());
    }
    // Salva a senha com encoding base64 simples (não é criptografia, apenas ofuscação)
    if (password && password.trim()) {
      const encodedPassword = btoa(password.trim());
      setCookie(REMEMBER_KEYS.PASSWORD, encodedPassword);
    }
  },
  clear: () => {
    deleteCookie(REMEMBER_KEYS.USER_ID);
    deleteCookie(REMEMBER_KEYS.USERNAME);
    deleteCookie(REMEMBER_KEYS.PASSWORD);
  },
  get: () => {
    const userId = getCookie(REMEMBER_KEYS.USER_ID);
    const username = getCookie(REMEMBER_KEYS.USERNAME);
    const encodedPassword = getCookie(REMEMBER_KEYS.PASSWORD);

    // Decodifica a senha se existir
    let password: string | null = null;
    if (encodedPassword && encodedPassword.trim()) {
      try {
        password = atob(encodedPassword.trim());
      } catch (e) {
        password = null;
      }
    }

    return {
      // Retorna null se for string vazia ou apenas espaços
      userId: userId && userId.trim() ? userId.trim() : null,
      username: username && username.trim() ? username.trim() : null,
      password
    };
  }
};

type Role = 'admin' | 'diretor' | 'gerente' | 'supervisor' | 'user';

interface UserData {
  id: string | number;
  usuario: string;
  base: number | string;
  role: Role;
  usuarioAmostras?: number;
  isAws?: boolean;
}

interface LoginResponse {
  token: string;
  user: UserData;
}

export interface AuthState {
  isAuthenticated: boolean;
  token: string | null;
  user: UserData | null; // Armazenar o objeto de usuário inteiro
  role: Role | null; // Armazenar o 'role' para acesso rápido
  login: (data: LoginResponse) => void;
  logout: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      isAuthenticated: false,
      token: null,
      user: null,
      role: null,

      login: (data: LoginResponse) => {
        try {

          set({
            isAuthenticated: true,
            token: data.token,
            user: data.user,
            role: data.user.role // Armazenar o 'role' separadamente
          });
        } catch (error) {
          console.error("Falha ao decodificar o token:", error);
          // Opcional: fazer logout se o token for inválido
          set({
            isAuthenticated: false,
            token: null,
            user: null,
            role: null
          });
        }
      },

      logout: async () => {
        // Chamar API de logout para remover sessão do banco
        await logoutAPI();

        // Limpar estado local
        set({
          isAuthenticated: false,
          token: null,
          user: null,
          role: null
        });
      },
    }),
    {
      name: 'auth-storage',
    }
  )
);
